/**
 * `tableRowResize` — drag-to-resize table ROW heights (2026-06-12, Chris ask).
 *
 * Tiptap's table extension ships COLUMN resizing (`Table.configure({ resizable: true })`)
 * but not row resizing — HTML table rows size to their content. This adds it.
 *
 * # Deliberate-affordance gating (2026-06-12 follow-up)
 *
 * The first cut armed a resize on *any* mousedown within a few px of a row's bottom edge —
 * which made an ordinary click near the bottom of a cell accidentally start a drag. Per Chris:
 * "the table width edit should only happen when a hover tooltip gives the option to avoid
 * accidental clicks." So the drag now starts ONLY from an explicit, visible grip:
 *   - `ResizableTableRow` extends the stock TableRow with a `height` attribute (rendered as
 *     `style="height: Npx"` on the <tr>; acts as a MIN height — content can still grow it).
 *   - `TableRowResize` is a ProseMirror plugin that, when the pointer nears a row's bottom edge,
 *     reveals a single shared **grip** element (a thin bar with a `title` tooltip and a
 *     `row-resize` cursor) docked to that edge. A drag begins only from a mousedown ON the grip —
 *     a click in the cell body never resizes. The grip hides as soon as the pointer leaves.
 *
 * Like column widths, row heights are EPHEMERAL: notes persist as markdown, which has no
 * table-dimension syntax, so the height is dropped on save and rows reset to content-height on
 * reload. This is an editing-time affordance, not stored geometry.
 *
 * Every DOM handler is wrapped so a resize bug can NEVER break table editing.
 */

import { Extension } from '@tiptap/core';
import TableRow from '@tiptap/extension-table-row';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

/** Pointer proximity (px) to a row's bottom edge that reveals the grip. */
const EDGE_PX = 6;
/** Floor on a dragged row height. */
const MIN_ROW_HEIGHT = 24;
/** Visible thickness (px) of the grip bar. */
const GRIP_THICKNESS = 6;

/** TableRow + a `height` attribute (rendered as an inline style; min-height semantics). */
export const ResizableTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      height: {
        default: null,
        parseHTML: (element) => {
          const raw = (element as HTMLElement).style?.height;
          const n = raw ? parseInt(raw, 10) : NaN;
          return Number.isFinite(n) ? n : null;
        },
        renderHTML: (attributes) => {
          const h = (attributes as { height?: number | null }).height;
          return h ? { style: `height: ${h}px` } : {};
        },
      },
    };
  },
});

const key = new PluginKey('tableRowResize');

/** Find the <tr> ancestor of an event target, or null. */
function rowElementFromEvent(event: MouseEvent): HTMLTableRowElement | null {
  let el = event.target as HTMLElement | null;
  while (el && el.nodeName !== 'TR') el = el.parentElement;
  return (el as HTMLTableRowElement | null) ?? null;
}

/** Resolve the document position of the tableRow node containing DOM `row`, or -1. */
function rowNodePos(view: EditorView, row: HTMLTableRowElement): number {
  try {
    const pos = view.posAtDOM(row, 0);
    const $pos = view.state.doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d--) {
      if ($pos.node(d).type.name === 'tableRow') return $pos.before(d);
    }
  } catch {
    /* posAtDOM can throw for a detached node mid-update — treat as not-found */
  }
  return -1;
}

export const TableRowResize = Extension.create({
  name: 'tableRowResize',

  addProseMirrorPlugins() {
    // A single shared grip element, lazily created and reused across all tables. It is the ONLY
    // thing that initiates a row-height drag — so the affordance is explicit and a stray click in
    // a cell can never resize a row. `armed` holds the row the grip is currently docked to.
    let grip: HTMLDivElement | null = null;
    let armed: { rowPos: number; rowHeight: number } | null = null;
    let drag: { startY: number; startHeight: number; rowPos: number } | null = null;
    let viewRef: EditorView | null = null;

    const hideGrip = (): void => {
      armed = null;
      if (grip) grip.style.display = 'none';
    };

    const onGripMouseDown = (event: MouseEvent): void => {
      try {
        if (event.button !== 0 || armed === null) return;
        drag = { startY: event.clientY, startHeight: armed.rowHeight, rowPos: armed.rowPos };
        // The grip stays put + the page-level move/up handlers drive the drag.
        if (grip) grip.classList.add('is-dragging');
        document.body.style.cursor = 'row-resize';
        event.preventDefault();
        event.stopPropagation();
      } catch {
        drag = null;
      }
    };

    const ensureGrip = (): HTMLDivElement => {
      if (grip) return grip;
      const el = document.createElement('div');
      el.className = 'aletheia-row-resize-grip';
      el.title = 'Drag to resize row';
      el.setAttribute('aria-hidden', 'true');
      el.style.display = 'none';
      el.addEventListener('mousedown', onGripMouseDown);
      document.body.appendChild(el);
      grip = el;
      return el;
    };

    const onPageMouseMove = (event: MouseEvent): void => {
      if (drag === null || viewRef === null) return;
      try {
        const delta = event.clientY - drag.startY;
        const next = Math.max(MIN_ROW_HEIGHT, Math.round(drag.startHeight + delta));
        viewRef.dispatch(viewRef.state.tr.setNodeAttribute(drag.rowPos, 'height', next));
        event.preventDefault();
      } catch {
        // A doc edit can invalidate rowPos mid-drag; abandon the drag rather than throw.
        drag = null;
        document.body.style.cursor = '';
        if (grip) grip.classList.remove('is-dragging');
      }
    };

    const onPageMouseUp = (): void => {
      if (drag === null) return;
      drag = null;
      document.body.style.cursor = '';
      if (grip) grip.classList.remove('is-dragging');
      hideGrip();
    };

    return [
      new Plugin({
        key,
        view: (view) => {
          viewRef = view;
          ensureGrip();
          window.addEventListener('mousemove', onPageMouseMove, true);
          window.addEventListener('mouseup', onPageMouseUp, true);
          return {
            destroy: () => {
              window.removeEventListener('mousemove', onPageMouseMove, true);
              window.removeEventListener('mouseup', onPageMouseUp, true);
              if (grip) {
                grip.removeEventListener('mousedown', onGripMouseDown);
                grip.remove();
                grip = null;
              }
              viewRef = null;
              armed = null;
              drag = null;
            },
          };
        },
        props: {
          handleDOMEvents: {
            // Reveal/dock the grip when the pointer nears a row's bottom edge. This NEVER starts a
            // drag — it only offers the affordance; the drag begins from the grip's own mousedown.
            mousemove: (view, event) => {
              try {
                if (drag !== null) return false; // an active drag is driven by the page handlers
                const row = rowElementFromEvent(event);
                if (row === null) {
                  hideGrip();
                  return false;
                }
                const rect = row.getBoundingClientRect();
                const nearBottom = Math.abs(event.clientY - rect.bottom) <= EDGE_PX;
                if (!nearBottom) {
                  // Keep the grip if the pointer is hovering the grip itself; else hide.
                  if (event.target !== grip) hideGrip();
                  return false;
                }
                const rowPos = rowNodePos(view, row);
                if (rowPos < 0) {
                  hideGrip();
                  return false;
                }
                const el = ensureGrip();
                armed = { rowPos, rowHeight: rect.height };
                el.style.display = 'block';
                el.style.left = `${rect.left}px`;
                el.style.width = `${rect.width}px`;
                el.style.top = `${rect.bottom - GRIP_THICKNESS / 2}px`;
                el.style.height = `${GRIP_THICKNESS}px`;
              } catch {
                hideGrip();
              }
              return false;
            },
            // Leaving the editor surface clears the affordance (unless mid-drag).
            mouseleave: () => {
              if (drag === null) hideGrip();
              return false;
            },
          },
        },
      }),
    ];
  },
});
